// The canvas header's history: Back and Forward through what this chat has opened, and the title as the "In this chat"
// switcher (DES1 CANVAS-HISTORY.md). One item looks exactly like today; the arrows and the chevron appear with two.
import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { CheckIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import canvasNavCopy from '@/i18n/ns/canvas-nav';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { useOutsidePress } from '@/lib/use-outside-press';
import { sameCanvasObject } from './canvas-navigation';
import type { CanvasLogItem } from './canvas-log';
import { useWorkspace } from './context';
import type { CanvasTarget } from './types';

const nameOf = (item: CanvasLogItem, fallback: string) => item.name ?? item.kind ?? fallback;

/** ⌘[ / ⌘] (Ctrl on Windows): the arrows' keys, while a canvas with history is on show. */
function useHistoryKeys() {
  const ws = useWorkspace();
  const latest = useRef(ws.canvasHistory);
  latest.current = ws.canvasHistory;
  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) return;
      if (event.key !== '[' && event.key !== ']') return;
      const history = latest.current;
      if (!history.shown) return;
      const moved = event.key === '[' ? history.goBack() : history.goForward();
      if (moved) event.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}

export function CanvasHistoryArrows({ fallback }: { fallback: string }) {
  const ws = useWorkspace();
  const copy = useNamespaceCopy(canvasNavCopy);
  useHistoryKeys();
  const { shown, back, forward } = ws.canvasHistory;
  if (!shown) return null;
  return (
    <span className="ws-canvas-arrows" data-slot="canvas-arrows">
      <button
        type="button"
        className="ws-icon-button"
        data-slot="canvas-history-back"
        disabled={!back}
        aria-label={
          back
            ? formatCopy(copy.back, { name: nameOf(back, fallback) })
            : copy.back.replace('{name}', '')
        }
        onClick={() => ws.canvasHistory.goBack()}
      >
        <ChevronLeftIcon aria-hidden="true" />
      </button>
      <button
        type="button"
        className="ws-icon-button ws-canvas-forward"
        data-slot="canvas-history-forward"
        disabled={!forward}
        aria-label={
          forward
            ? formatCopy(copy.forward, { name: nameOf(forward, fallback) })
            : copy.forward.replace('{name}', '')
        }
        onClick={() => ws.canvasHistory.goForward()}
      >
        <ChevronRightIcon aria-hidden="true" />
      </button>
    </span>
  );
}

/** The title and kind of the item on show, as the switcher button when the chat has more than one. */
export function CanvasSwitcher({
  target,
  title,
}: {
  target: CanvasTarget;
  /** The kind label the header showed before (today's look). */
  title: string;
}) {
  const ws = useWorkspace();
  const copy = useNamespaceCopy(canvasNavCopy);
  const locale = useChatInterfaceLocale();
  const { items, shown, forward } = ws.canvasHistory;
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLSpanElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  // The list is drawn at the body (a fixed sheet cannot live inside the header's size container).
  const [place, setPlace] = useState<CSSProperties>({});
  // Below 900px of header, Forward is the first row of the list instead of an arrow.
  const [narrow, setNarrow] = useState(false);
  const current = items.find((item) => sameCanvasObject(item.target, target)) ?? null;
  const label = current?.name ? `${current.name} · ${current.kind ?? title}` : title;
  // The trigger and the list drawn at the body are both inside.
  useOutsidePress(open, [root, pop], () => setOpen(false));
  useEffect(() => {
    if (!open) return;
    pop.current
      ?.querySelector<HTMLElement>('[aria-current="true"], [role="option"]:not(:disabled)')
      ?.focus();
  }, [open]);
  if (!shown) return <span className="ws-canvas-kind">{title}</span>;
  const close = (refocus: boolean) => {
    setOpen(false);
    if (refocus) trigger.current?.focus();
  };
  const onKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close(true);
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const rows = [
      ...(pop.current?.querySelectorAll<HTMLElement>('[role="option"]:not(:disabled)') ?? []),
    ];
    const at = rows.indexOf(document.activeElement as HTMLElement);
    const next = rows[(at + (event.key === 'ArrowDown' ? 1 : -1) + rows.length) % rows.length];
    if (next) {
      event.preventDefault();
      next.focus();
    }
  };
  const when = (at: number) =>
    new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(new Date(at));
  const kindLine = (item: CanvasLogItem) =>
    [
      item.kind,
      'versionNumber' in item.target && typeof item.target.versionNumber === 'number'
        ? `v${item.target.versionNumber}`
        : null,
      when(item.at),
    ]
      .filter(Boolean)
      .join(' · ');
  return (
    <span className="ws-canvas-switcher" ref={root} onKeyDown={onKey}>
      <button
        type="button"
        ref={trigger}
        className="ws-canvas-switch"
        data-slot="canvas-switcher"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={formatCopy(copy.switcherLabel, { name: current?.name ?? title })}
        onClick={() => {
          const box = trigger.current?.getBoundingClientRect();
          setNarrow(
            (trigger.current?.closest('.ws-canvas-top')?.getBoundingClientRect().width ?? 0) < 900,
          );
          if (box)
            setPlace({
              '--pop-top': `${Math.round(box.bottom + 6)}px`,
              '--pop-left': `${Math.max(12, Math.min(Math.round(box.left), window.innerWidth - 332))}px`,
            } as CSSProperties);
          setOpen((value) => !value);
        }}
      >
        <span className="ws-canvas-kind">{label}</span>
        <ChevronDownIcon aria-hidden="true" />
      </button>
      {open &&
        createPortal(
          <div className="pc-portal ws-canvas-switch-portal" onKeyDown={onKey}>
            <div
              ref={pop}
              style={place}
              data-narrow={narrow || undefined}
              className="ws-canvas-switch-pop"
              role="dialog"
              aria-label={copy.inThisChat}
              data-slot="canvas-switch-list"
            >
              <div className="ws-canvas-switch-handle" aria-hidden="true" />
              <p className="ws-canvas-switch-title">{copy.inThisChat}</p>
              <ul role="listbox" aria-label={copy.inThisChat}>
                {forward && (
                  <li role="presentation" className="ws-canvas-switch-forward">
                    <button
                      type="button"
                      role="option"
                      aria-selected="false"
                      onClick={() => {
                        close(false);
                        ws.canvasHistory.goForward();
                      }}
                    >
                      <ChevronRightIcon aria-hidden="true" />
                      <span>{formatCopy(copy.forward, { name: nameOf(forward, title) })}</span>
                    </button>
                  </li>
                )}
                {items.map((item) => {
                  const isCurrent = current === item;
                  return (
                    <li role="presentation" key={`${item.target.kind}:${item.target.id}`}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={isCurrent}
                        aria-current={isCurrent ? 'true' : undefined}
                        disabled={item.unavailable}
                        onClick={() => {
                          close(false);
                          if (!isCurrent) ws.canvasHistory.open(item);
                        }}
                      >
                        <span className="ws-canvas-switch-thumb" aria-hidden="true">
                          {nameOf(item, title).slice(0, 1).toUpperCase()}
                        </span>
                        <span className="ws-canvas-switch-text">
                          <strong>{nameOf(item, title)}</strong>
                          <small>{item.unavailable ? copy.unavailable : kindLine(item)}</small>
                        </span>
                        {isCurrent && (
                          <>
                            <CheckIcon aria-hidden="true" />
                            <span className="sr-only">{copy.current}</span>
                          </>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>,
          document.body,
        )}
    </span>
  );
}
